import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgSplitButton } from "@intelligems/ig-theme/IgSplitButton";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgIconColors } from "@intelligems/ig-design-tokens/constants";
import { CircularProgress, Stack } from "@mui/material";

import type { RuleSaveApplyState } from "./ruleSaveApplyState";

export interface RuleMoreAction {
  label: string;
  /** Why the action is unavailable; `undefined` when it is available. */
  disabledHint: string | undefined;
  onSelect: () => void;
}

/**
 * The rule header's right controls. Edits autosave, so nothing here saves:
 *
 * - A rule no experience uses shows Done, which only exits.
 * - A rule in use shows "Apply to N experiences", disabled while the draft
 *   matches live, with "Discard changes" beside it once it does not. The
 *   caret holds the alternatives (Save as new rule, Run checks).
 */
export const RuleSaveApplyActions = ({
  state,
  isDiscarding,
  onDone,
  onDiscard,
  onApply,
  moreActions,
}: {
  state: RuleSaveApplyState;
  isDiscarding: boolean;
  onDone: () => void;
  onDiscard: () => void;
  onApply: () => void;
  moreActions: readonly RuleMoreAction[];
}) => {
  if (state.showDone) {
    return (
      <IgButton color="primary" onClick={onDone}>
        Done
      </IgButton>
    );
  }
  if (!state.showApply) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={3}>
      {state.showDiscard ? (
        <IgTooltip title={state.discardDisabledHint}>
          <span>
            <IgButton
              variant="outlined"
              color="secondary"
              loading={isDiscarding}
              disabled={state.discardDisabledHint !== undefined || isDiscarding}
              onClick={onDiscard}
            >
              Discard changes
            </IgButton>
          </span>
        </IgTooltip>
      ) : null}
      <IgSplitButton
        color="primary"
        // Figma: the menu hangs under the caret, right-aligned (ENG-7894).
        menuAlignment="end"
        startIcon={
          state.isApplying ? (
            // Icon/On-dark/Default (ENG-7894): the button is disabled while
            // it runs, and inheriting its disabled label grey hid the spinner.
            <CircularProgress
              size={14}
              aria-hidden
              sx={{ color: IgIconColors.dark }}
            />
          ) : undefined
        }
        disabled={state.applyDisabledHint !== undefined}
        tooltip={state.isApplying ? undefined : state.applyDisabledHint}
        menuOptions={[
          {
            key: state.applyLabel,
            hidden: true,
            action: () => {
              if (state.applyDisabledHint === undefined) {
                onApply();
              }
            },
          },
          ...moreActions.map((action) => ({
            key: action.label,
            disabledMessage: action.disabledHint,
            action: action.onSelect,
          })),
        ]}
      />
    </Stack>
  );
};
